import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  GET_RISK_RESPONSIBLES_QUERY,
  GET_PARTNER_QUERY,
  GET_FOLDER_HEADER_ALPHA_QUERY,
  GET_RISK_ANALYSIS_TYPES_QUERY,
  GET_PARTNERS_LIST_QUERY
} from '../queries';

import { OFFER_TYPE_QUERY } from '../../../../../offer/edit/financials/additionalData/queries';

import { UPDATE_FOLDER_GENERAL_INFO_MUTATION, SAVE_NEW_FOLDER_MUTATION, CALCULATE_APPROVAL_MUTATION } from '../mutations';

import { FOLDER_GENERAL_INFO_UPDATED, NEW_FOLDER_SAVED } from '../subscriptions';
import { shape, required } from '@totalsoft/pure-validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import FolderGeneralInfoCardAlpha from './FolderGeneralInfoCardAlpha';
import offerType from 'constants/offerType';
import { useDirtyInfo } from '@totalsoft/change-tracking-react';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import useDebounce from 'utils/hooks/useDebounce';
import { useDictionary } from 'features/offer/edit/financials/additionalData/hooks/useDictionary';
import { useMutation } from '@apollo/client';
import { showError } from 'actions/actionCreators/snackbarActions';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { getInt } from 'utils/functions';

const validatorNewFolderHeader = shape({
  clientId: required
});

const FolderGeneralInfoContainerAlpha = ({ documentId, siteId, documentTypeId, isFacilityApproval, history }) => {
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();
  const [validationNewFolderHeader, validateNewFolderHeader] = useDirtyFieldValidation(validatorNewFolderHeader, dirtyInfo);
  const localGeneralInfo = {
    generalInfo: {
      client: {},
      isFacilityApproval,
      partnerGroup: '',
      partnerGroupId: null,
      folderTypeId: isFacilityApproval ? offerType.facilityOffer.id : offerType.newRequest.id,
      riskAnalysisTypeId: null
    }
  };
  const [state, setState] = useState(localGeneralInfo);
  const [responsibleOptions, setResponsibleOptions] = useState([]);
  const [partnersOptions, setPartnersOptions] = useState([]);

  const folderTypes = useDictionary(OFFER_TYPE_QUERY, {
    variables: { documentTypeId },
    skip: !documentTypeId
  });

  const riskAnalysisTypes = useDictionary(GET_RISK_ANALYSIS_TYPES_QUERY, {
    variables: { documentTypeId },
    skip: !documentTypeId,
    onCompleted: data => {
      setState(previousState => ({
        ...previousState,
        generalInfo: {
          ...previousState.generalInfo,
          riskAnalysisTypeId: data && data.riskAnalysisTypes.find(item => item.implicit === true)?.dictionaryItemId
        }
      }));
    }
  });

  useQueryWithErrorHandling(GET_RISK_RESPONSIBLES_QUERY, {
    onCompleted: data => {
      const responsibles = data?.riskResponsibles;
      if (responsibles)
        setResponsibleOptions(
          responsibles.map(item => {
            return { responsibleId: item.responsibleId, responsibleName: item.name };
          })
        );
    }
  });
  const responsiblesOptionsDebounced = useDebounce(responsibleOptions, 500);

  const { storeKey } = useServerErrorHandling({
    effect: _ => {
      setSaving(false);
    }
  });

  useQueryWithErrorHandling(GET_FOLDER_HEADER_ALPHA_QUERY, {
    variables: { documentId: getInt(documentId), siteId: getInt(siteId) },
    skip: !documentId,
    onCompleted: data => {
      setState(previousState => ({
        ...previousState,
        generalInfo: {
          ...data.folderHeaderAlpha.generalInfo,
          client: data.folderHeaderAlpha.client,
          partnerGroup: data.folderHeaderAlpha.partnerGroup,
          partnerGroupId: data.folderHeaderAlpha.partnerGroupId
        }
      }));
    }
  });

  useQueryWithErrorHandling(GET_PARTNERS_LIST_QUERY, {
    variables: { search: '' },
    skip: documentId,
    onCompleted: data => {
      const partnersList = data?.partnersList;
      if (partnersList)
        setPartnersOptions(
          partnersList.map(item => {
            return { clientId: item.id, uniqueId: item.uniqueId, clientName: item.partnerName };
          })
        );
    }
  });
  const partnersOptionsDebounced = useDebounce(partnersOptions, 500);

  useQueryWithErrorHandling(GET_PARTNER_QUERY, {
    variables: { uniqueId: state.generalInfo.client?.uniqueId },
    skip: !state.generalInfo.client?.uniqueId || documentId,
    onCompleted: data => {
      onGeneralInfoChange({ clientName: data.partner.partnerName }, 'client');
    }
  });

  const [saveNewFolder] = useMutation(SAVE_NEW_FOLDER_MUTATION, {
    onError: error => {
      showError(error.message);
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.saveNewFolder;
      storeKey({ correlationId });
    }
  });

  const [updateFolderGeneralInfo] = useMutation(UPDATE_FOLDER_GENERAL_INFO_MUTATION, {
    onError: error => {
      showError(error.message);
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.updateFolderGeneralInfo;
      storeKey({ correlationId });
    }
  });

  const [calculateApproval] = useMutation(CALCULATE_APPROVAL_MUTATION, {
    onError: error => {
      showError(error.message);
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.calculateApproval;
      storeKey({ correlationId });
    }
  });

  const onCalculateApproval = () => {
    calculateApproval({
      variables: {
        input: {
          documentId,
          siteId
        }
      }
    });
  };

  useEffect(() => {
    validateNewFolderHeader(state.generalInfo.client, dirtyInfo);
  }, [state.generalInfo.client, dirtyInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRedirect = (documentId, siteId) => {
    history.push(`/folders/${documentId}/${siteId}`);
  };

  const onSaveNewCallBack = (documentId, siteId) => {
    if (!documentId) return;
    onRedirect(documentId, siteId);
  };

  const onSave = useCallback(() => {
    if (!validateNewFolderHeader(state.generalInfo.client)) return;
    setSaving(true);
    if (!documentId) {
      const dataNewFolder = {
        responsibleId: state.generalInfo.responsibleId,
        responsibleName: state.generalInfo.responsibleName,
        accountManagerId: state.generalInfo.accountManagerId,
        accountManagerName: state.generalInfo.accountManagerName,
        folderTypeId: state.generalInfo.folderTypeId,
        folderType: state.generalInfo.folderType,
        clientId: state.generalInfo.client.clientId,
        clientName: state.generalInfo.client.clientName,
        uniqueId: state.generalInfo.client.uniqueId,
        riskAnalysisTypeId: state.generalInfo.riskAnalysisTypeId,
        isFacilityApproval: isFacilityApproval
      };
      saveNewFolder({
        variables: {
          input: dataNewFolder
        }
      });
    } else {
      const dataForUpdate = {
        documentId: documentId,
        siteId: siteId,
        responsibleId: state.generalInfo.responsibleId,
        responsibleName: state.generalInfo.responsibleName,
        accountManagerId: state.generalInfo.accountManagerId,
        accountManagerName: state.generalInfo.accountManagerName,
        folderTypeId: state.generalInfo.folderTypeId,
        folderType: state.generalInfo.folderType,
        riskAnalysisTypeId: state.generalInfo.riskAnalysisTypeId
      };
      updateFolderGeneralInfo({
        variables: {
          input: dataForUpdate
        }
      });
    }
  }, [
    documentId,
    isFacilityApproval,
    saveNewFolder,
    siteId,
    state.generalInfo.accountManagerId,
    state.generalInfo.accountManagerName,
    state.generalInfo.client,
    state.generalInfo.folderType,
    state.generalInfo.folderTypeId,
    state.generalInfo.responsibleId,
    state.generalInfo.responsibleName,
    state.generalInfo.riskAnalysisTypeId,
    updateFolderGeneralInfo,
    validateNewFolderHeader
  ]);

  const onGeneralInfoChange = useCallback(
    (value, prop = null) => {
      setState(generalInfoUpdater(value, prop));
      setDirtyInfo(`${prop}`);
      setIsDirty(true);
    },
    [setDirtyInfo]
  );

  const generalInfoUpdater =
    (value, prop = null) =>
    prevState => {
      if (prop === 'client')
        return {
          generalInfo: { ...prevState.generalInfo, client: { ...prevState.generalInfo.client, ...value } }
        };
      return {
        generalInfo: prop ? { ...prevState.generalInfo, [prop]: value } : { ...prevState.generalInfo, ...value }
      };
    };

  const openLinkedDocument = useCallback(
    (documentId, documentSiteId) => e => {
      e.stopPropagation();
      if (isFacilityApproval) window.open(`/facilities/${documentId}/${documentSiteId}/generalData`, '_blank');
    },
    [isFacilityApproval]
  );

  useGlobalSubscription(NEW_FOLDER_SAVED, {
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      setSaving(false);
      setIsDirty(false);
      onSaveNewCallBack(data.newFolderSaved.documentId, data.newFolderSaved.siteId);
    }
  });

  const isNewDocument = useMemo(() => getInt(documentId) === null || getInt(siteId) === null, [documentId, siteId]);

  useGlobalSubscription(FOLDER_GENERAL_INFO_UPDATED, {
    variables: {
      documentId,
      siteId
    },
    skip: isNewDocument,
    onSubscriptionData: ({ client }) => {
      setSaving(false);
      setIsDirty(false);
      client.cache.evict({
        id: 'ROOT_QUERY',
        fieldName: `folderHeaderAlpha({"documentId":${documentId},"siteId":${siteId}})`
      });
      client.cache.gc();
    }
  });

  return (
    <FolderGeneralInfoCardAlpha
      generalInfo={state.generalInfo}
      savingGeneralInfo={saving}
      onSave={onSave}
      isDirty={isDirty}
      validation={validationNewFolderHeader}
      isNewFolder={!documentId}
      onGeneralInfoChange={onGeneralInfoChange}
      responsiblesOptions={responsiblesOptionsDebounced}
      partnersOptions={partnersOptionsDebounced}
      folderTypes={folderTypes.data}
      openLinkedDocument={openLinkedDocument}
      isFacilityApproval={isFacilityApproval}
      onCalculateApproval={onCalculateApproval}
      riskAnalysisTypes={riskAnalysisTypes.data}
    />
  );
};

FolderGeneralInfoContainerAlpha.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  documentTypeId: PropTypes.number,
  isFacilityApproval: PropTypes.bool,
  history: PropTypes.object.isRequired
};

export default FolderGeneralInfoContainerAlpha;
